import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { RoleRailItem } from 'src/app/models/roleRailItem.model';

/**
 * Role rail — the master column of the Roles-tab master–detail layout (DASH-3179, design-spec §4.2,
 * §5.5, component spec §6). Keeps the object search pinned while the admin steps through roles, which
 * is why it is a rail rather than a list-page → detail route.
 *
 * Presentational only: the container pre-computes {@link RoleRailItem}s (name, granted count, dirty
 * and system flags) and owns selection/creation. The rail owns just its own search box.
 *
 * DS conformance: reuses the CoreGrid list chrome (search + create-button placement) per the
 * deliberate DS-12 deviation; no `mat-accordion`, `mat-elevation`, Font Awesome, `::ng-deep` or
 * `!important`; nothing interactive is nested inside the row button.
 */
@Component({
  standalone: false,
  selector: 'app-role-rail',
  templateUrl: './role-rail.component.html',
  styleUrls: ['./role-rail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoleRailComponent implements OnChanges {
  @Input() items: RoleRailItem[] = [];
  @Input() selectedKey: string | null = null;

  @Output() selectRole = new EventEmitter<RoleRailItem>();
  @Output() addRole = new EventEmitter<void>();

  searchTerm = '';
  filteredItems: RoleRailItem[] = [];

  ngOnChanges(): void {
    this.applyFilter();
  }

  onSearchChange(term: string): void {
    this.searchTerm = term;
    this.applyFilter();
  }

  onSelect(item: RoleRailItem): void {
    this.selectRole.emit(item);
  }

  onAdd(): void {
    this.addRole.emit();
  }

  public trackByKey = (_: number, item: RoleRailItem): string => item.key;

  private applyFilter(): void {
    const term = this.searchTerm.trim().toLowerCase();
    this.filteredItems = term
      ? this.items.filter(item => `${item.name} ${item.description}`.toLowerCase().includes(term))
      : this.items;
  }
}
